/**
 * The virtual account's Cashflow forecast tab (TMMAPP-0108; audit 06 § A10, v2 L471–620): the shared
 * `ForecastCard` (TMMAPP-0102) on the virtual account's one projection (MM-D-53) — KPIs Balance
 * today, Projected balance with its delta over the horizon, and Lowest point "{date} · after
 * {event}"; v2's note; and, in place of the account's "Planned events", the "Cashflow forecast"
 * list: one row per event (the register's rows grouped by the served `eventKey`), its allocation
 * chips, a PLAN / EDITED tag and the running balance, then the row menu. The envelope filter (D1)
 * sits in the card's filter slot. The graph and the list read the same response; a drill-down is
 * the same endpoint with a narrower window, never recomputed here.
 *
 * TMMAPP-0112: the virtual account's own expected items (TMMAPI-0148, MM-D-122) are in the list,
 * tagged Expected; an overdue one sits at today with an "Overdue" tag and "was {date}". A writer's row
 * menu marks a plan line received or dismisses it, edits or deletes an expected item, and "+ Expected
 * item" adds one; the match prompt sits above the list. A series row's Edit / Delete opens C12's scope
 * dialog (the owner's; a published row offers no series actions), and the sheet and the editors are
 * the one series host (`useSeriesHost`).
 */
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Envelope } from '@/api/envelopes';
import { plannedApi } from '@/api/planned';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import { ErrorState } from '@/components/ErrorState';
import { ListTableHeader } from '@/components/ListTableHeader';
import { MoneyParts, MoneyText } from '@/components/MoneyText';
import { registerAmountColor } from '@/components/moneyTone';
import { ForecastCard, type ForecastKpi } from '@/features/cashflow/ForecastCard';
import { HORIZON_SCHEMA } from '@/features/cashflow/horizon';
import { scopeKey, type DrillDown } from '@/features/cashflow/types';
import { useProjection } from '@/features/cashflow/useProjection';
import { useSeriesHost } from '@/features/cashflow/useSeriesHost';
import { plannedErrorKey } from '@/features/planned/plannedErrors';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useUrlState } from '@/shared/useUrlState';
import { OverflowMenu } from '@/shell/OverflowMenu';
import { resolveTokens } from '@/theme/theme';
import { AddIcon } from '@/components/icons';
import { comingFromAccounts } from './comingFromAccounts';
import { forecastGroups, type ForecastGroup } from './forecastGroups';
import { PlannedEventDialog, type EventScope } from './PlannedEventDialog';
import { useExpectedItems, type ExpectedRow } from './useExpectedItems';

const DATE_SHORT = { month: 'short', day: 'numeric' } as const;
/** Coming from your accounts shows a slice's next three dates, then "+{n} more". */
const COMING_DATES = 3;
const TAG_LABEL = {
  expected: 'virtualAccount.forecast.expected',
  plan: 'virtualAccount.forecast.plan',
  edited: 'virtualAccount.forecast.edited',
} as const;

interface VirtualAccountForecastTabProps {
  virtualAccount: VirtualAccountDetail;
  envelopes: Envelope[];
  owner: boolean;
  /** The owner or a Manager (served `canManage`): writes the expected items and settles plan lines. */
  canWrite: boolean;
  /** The envelope filter (0 = every envelope); the page owns it in the URL. */
  envelopeId: number;
  onEnvelopeChange: (id: number) => void;
  onNotify: (message: string) => void;
}

export function VirtualAccountForecastTab({
  virtualAccount,
  envelopes,
  owner,
  canWrite,
  envelopeId,
  onEnvelopeChange,
  onNotify,
}: VirtualAccountForecastTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const [{ h: horizon }] = useUrlState(HORIZON_SCHEMA);
  const scope = useMemo(
    () => ({
      kind: 'virtualAccount' as const,
      virtualAccountId: virtualAccount.id,
      ...(envelopeId > 0 ? { envelopeId } : {}),
    }),
    [virtualAccount.id, envelopeId],
  );
  const key = scopeKey(scope);
  const [drill, setDrill] = useState<DrillDown | null>(null);
  const [prev, setPrev] = useState({ key, horizon });
  if (prev.key !== key || prev.horizon !== horizon) {
    setPrev({ key, horizon });
    if (drill !== null) setDrill(null);
  }
  const { status, error, full, table, fromToday, reload } = useProjection(scope, horizon, drill);
  const currency = virtualAccount.currency;
  const host = useSeriesHost({ currency, onChanged: reload, onNotify });
  const expected = useExpectedItems({ virtualAccount, canWrite, onChanged: reload, onNotify });
  const [event, setEvent] = useState<{
    action: 'edit' | 'delete';
    group: ForecastGroup;
    seriesId: number;
  } | null>(null);

  async function openAccount(seriesId: number) {
    try {
      const series = await plannedApi.getSeries(seriesId);
      void navigate(`/me/accounts/${series.accountId}?tab=recurring`);
    } catch (err) {
      onNotify(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
    }
  }

  /** C12's Edit: the occurrence sheet for this event, else the series editor from here or whole. */
  function editScope(seriesId: number, originalOn: string, scopeChoice: EventScope) {
    setEvent(null);
    if (scopeChoice === 'occurrence') void host.openOccurrence(seriesId, originalOn);
    else if (scopeChoice === 'following') void host.openEditor('following', seriesId, originalOn);
    else void host.openEditor('series', seriesId);
  }

  if (status === 'loading' && full === null) {
    return (
      <Stack spacing={2.5}>
        <Skeleton variant="rounded" height={320} />
        <Skeleton variant="rounded" height={160} />
      </Stack>
    );
  }
  if (status === 'error' && full === null) {
    return (
      <ErrorState
        variant="section"
        error={error}
        what={t('errors.what.cashflow')}
        onRetry={() => void reload()}
      />
    );
  }
  if (full === null) return null;
  const expectedLine = (group: ForecastGroup) =>
    group.planLineId !== null ? expected.lines.get(group.planLineId) : undefined;
  const nameOf = (group: ForecastGroup) => expectedLine(group)?.name ?? group.name;

  const own = full.chartSeries[0];
  const startMinor = own?.points[0]?.balanceMinor ?? null;
  const endMinor = full.endMinor;
  const delta = startMinor !== null && endMinor !== null ? endMinor - startMinor : null;
  const lowest = full.minimum;
  const groups = forecastGroups((table ?? full).register);
  const lowestAfter =
    lowest === null ? null : groups.find((group) => group.runningMinor === lowest.balanceMinor);
  const chart = full.chartSeries
    .slice(0, 1)
    .map((series) => ({ ...series, color: 'brand' as const }));

  const kpis: ForecastKpi[] = [
    {
      id: 'today',
      label: t('virtualAccount.forecast.today'),
      value: <MoneyText minor={startMinor} currency={currency} variant="figureSm" />,
    },
    {
      id: 'projected',
      label: t('virtualAccount.forecast.projected'),
      value: <MoneyText minor={endMinor} currency={currency} variant="figureSm" />,
      ...(delta !== null
        ? {
            note: (
              <Typography
                variant="caption"
                component="span"
                data-delta=""
                sx={{ color: delta < 0 ? tokens.moneyNegative : tokens.moneyPositive }}
              >
                {t('forecast.overHorizon', {
                  delta: formatMoneyMinor(delta, currency, locale, true),
                })}
              </Typography>
            ),
          }
        : {}),
    },
    ...(lowest !== null
      ? [
          {
            id: 'lowest',
            label: t('forecast.lowestPoint'),
            value: <MoneyText minor={lowest.balanceMinor} currency={currency} variant="figureSm" />,
            note:
              lowestAfter !== null && lowestAfter !== undefined && nameOf(lowestAfter).length > 0
                ? t('forecast.lowestCause', {
                    date: formatCivilDate(lowest.date, locale),
                    payee: nameOf(lowestAfter),
                  })
                : formatCivilDate(lowest.date, locale),
          },
        ]
      : []),
  ];

  const filter = (
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <InputLabel id="virtual-account-envelope">{t('cashflow.filterEnvelope')}</InputLabel>
      <Select
        labelId="virtual-account-envelope"
        label={t('cashflow.filterEnvelope')}
        value={envelopeId > 0 ? String(envelopeId) : 'all'}
        onChange={(event) =>
          onEnvelopeChange(event.target.value === 'all' ? 0 : Number(event.target.value))
        }
      >
        <MenuItem value="all">{t('cashflow.allEnvelopes')}</MenuItem>
        {envelopes.map((envelope) => (
          <MenuItem key={envelope.id} value={String(envelope.id)}>
            {envelope.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const columns = '56px minmax(168px, 0.8fr) minmax(0, 1fr) 88px 28px';
  const money = (minor: number | null) =>
    minor === null ? '—' : <MoneyParts minor={minor} currency={currency} />;
  const toneOf = (minor: number | null) =>
    minor !== null && minor < 0 ? tokens.moneyNegative : tokens.text2;

  const menuFor = (group: ForecastGroup) => {
    const items: { key: string; label: string; run: () => void }[] = [];
    const line = expectedLine(group);
    if (group.plan && group.envelopeId !== null && line === undefined) {
      items.push({
        key: 'envelope',
        label: t('virtualAccount.forecast.openEnvelope'),
        run: () => void navigate(`/v/${virtualAccount.id}/e/${group.envelopeId}`),
      });
    }
    if (canWrite && group.planLineId !== null) {
      // A plan line's occurrence: the virtual account's own, or an envelope's (MM-D-122).
      const row: ExpectedRow = {
        planLineId: group.planLineId,
        envelopeId: line !== undefined ? null : group.envelopeId,
        originalOn: group.originalOn ?? group.date,
        name: nameOf(group),
      };
      items.push(
        {
          key: 'received',
          label: t('expected.markReceived'),
          run: () => void expected.markReceived(row),
        },
        { key: 'dismiss', label: t('expected.dismiss'), run: () => void expected.dismiss(row) },
      );
      if (line !== undefined) {
        items.push(
          { key: 'editLine', label: t('expected.edit'), run: () => expected.openEdit(line) },
          { key: 'deleteLine', label: t('expected.delete'), run: () => expected.askDelete(line) },
        );
      }
    }
    if (owner && group.seriesId !== null) {
      const seriesId = group.seriesId;
      items.push(
        {
          key: 'edit',
          label: t('virtualAccount.forecast.edit'),
          run: () => setEvent({ action: 'edit', group, seriesId }),
        },
        {
          key: 'delete',
          label: t('virtualAccount.forecast.delete'),
          run: () => setEvent({ action: 'delete', group, seriesId }),
        },
        {
          key: 'account',
          label: t('virtualAccount.forecast.openAccount'),
          run: () => void openAccount(seriesId),
        },
      );
    }
    if (items.length === 0) return <Box sx={{ width: 28 }} />;
    return (
      <OverflowMenu
        variant="card"
        title={nameOf(group)}
        label={t('virtualAccount.forecast.rowActions', { name: nameOf(group) })}
      >
        {(close) =>
          items.map((item) => (
            <MenuItem
              key={item.key}
              onClick={() => {
                close();
                item.run();
              }}
            >
              {item.label}
            </MenuItem>
          ))
        }
      </OverflowMenu>
    );
  };

  const chips = (group: ForecastGroup, large: boolean) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: large ? '5px' : '4px', minWidth: 0 }}>
      {group.parts.map((part, index) => (
        <Box
          key={`${part.sliceRef ?? part.planLineId ?? index}`}
          component="span"
          data-forecast-chip=""
          sx={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: large ? '6px' : '5px',
            maxWidth: '100%',
            minWidth: 0,
            border: `1px solid ${tokens.line}`,
            borderRadius: '3px',
            p: large ? '5px 9px' : '2px 7px',
            whiteSpace: 'nowrap',
          }}
        >
          <Box
            component="span"
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: large ? 12 : 11,
              color: tokens.text2,
              fontWeight: part.edited === true ? 500 : 400,
            }}
          >
            {part.unassigned ? t('virtualAccount.unassigned') : (part.envelopeName ?? '—')}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: large ? 12 : 11,
              fontWeight: 500,
              fontVariantNumeric: 'tabular-nums',
              // A list amount (contract v2 §13, TMMAPP-0154 §A 1).
              color: registerAmountColor(tokens, part.amountMinor ?? 0),
            }}
          >
            {part.amountMinor === null ? (
              '—'
            ) : (
              <MoneyParts minor={part.amountMinor} currency={currency} signed />
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );

  const tag = (group: ForecastGroup) => {
    const kind =
      expectedLine(group) !== undefined
        ? 'expected'
        : group.plan
          ? 'plan'
          : group.edited
            ? 'edited'
            : null;
    return (
      <>
        {kind !== null && (
          <Typography
            component="span"
            variant="micro"
            data-forecast-tag={kind}
            sx={{ flex: '0 0 auto', color: kind === 'plan' ? tokens.warningText : tokens.link }}
          >
            {t(TAG_LABEL[kind])}
          </Typography>
        )}
        {group.overdue && (
          <>
            <Typography
              component="span"
              variant="micro"
              data-forecast-tag="overdue"
              sx={{ flex: '0 0 auto', color: tokens.warningText }}
            >
              {t('expected.overdue')}
            </Typography>
            {group.originalOn !== null && (
              <Typography
                component="span"
                data-overdue-was=""
                sx={{ flex: '0 0 auto', fontSize: 12, color: tokens.text2 }}
              >
                {t('expected.was', { date: formatCivilDate(group.originalOn, locale, DATE_SHORT) })}
              </Typography>
            )}
          </>
        )}
      </>
    );
  };

  const list = (
    <Paper component="section" aria-labelledby="va-forecast-list" sx={{ p: 0, overflow: 'hidden' }}>
      <Stack
        direction="row"
        data-card-header=""
        sx={{
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          p: '16px 20px',
          borderBottom: `1px solid ${tokens.line}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography id="va-forecast-list" variant="h3" component="h2">
            {t('envelope.tabCashflow')}
          </Typography>
          <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
            {t('virtualAccount.forecast.listSub')}
          </Typography>
          {/* One plan for bank money (TMMAPP-0173 item 7): what an expected item is for. */}
          <Typography
            variant="caption"
            component="p"
            data-expected-help=""
            sx={{ color: tokens.text2 }}
          >
            {t('expected.bankHelp')}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: tokens.text2 }}>
          {t('virtualAccount.forecast.count', { count: groups.length })}
        </Typography>
        {canWrite && (
          <Button size="small" startIcon={<AddIcon />} onClick={expected.openCreate}>
            {t('expected.add')}
          </Button>
        )}
      </Stack>
      {wide && (
        <ListTableHeader columns={columns}>
          <span>{t('forecast.colDate')}</span>
          <span>{t('forecast.colEvent')}</span>
          <span>{t('virtualAccount.forecast.colAllocations')}</span>
          <Box component="span" sx={{ textAlign: 'end' }}>
            {t('envelope.colBalance')}
          </Box>
          <span />
        </ListTableHeader>
      )}
      <Box
        data-forecast-start=""
        sx={{
          display: wide ? 'grid' : 'flex',
          flexDirection: 'column',
          gridTemplateColumns: columns,
          columnGap: 1,
          gap: wide ? undefined : '3px',
          alignItems: wide ? 'center' : 'flex-start',
          p: wide ? '10px 20px' : '14px 16px',
          bgcolor: tokens.surface2,
          borderBottom: `1px solid ${tokens.line}`,
        }}
      >
        <Box
          component="span"
          sx={{ fontSize: 12, color: tokens.text2, fontVariantNumeric: 'tabular-nums' }}
        >
          {formatCivilDate(full.from, locale, DATE_SHORT)}
        </Box>
        <Box component="span" sx={{ fontSize: 13, fontWeight: 500 }}>
          {t('virtualAccount.forecast.start')}
        </Box>
        <Box component="span" sx={{ fontSize: 12, color: tokens.text2 }}>
          {t('virtualAccount.forecast.startNote')}
        </Box>
        <Box
          component="span"
          sx={{
            textAlign: wide ? 'end' : 'start',
            fontSize: wide ? 13 : 18,
            fontWeight: wide ? 500 : 400,
            fontVariantNumeric: 'tabular-nums',
            color: startMinor !== null && startMinor < 0 ? tokens.moneyNegative : tokens.text,
          }}
        >
          {money(startMinor)}
        </Box>
        {wide && <span />}
      </Box>
      {groups.map((group) =>
        wide ? (
          <Box
            key={group.key}
            data-forecast-row=""
            sx={{
              display: 'grid',
              gridTemplateColumns: columns,
              columnGap: 1,
              alignItems: 'flex-start',
              p: '9px 20px',
              borderBottom: `1px solid ${tokens.line}`,
              '&:hover': { bgcolor: tokens.hover },
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 12,
                color: tokens.text2,
                fontVariantNumeric: 'tabular-nums',
                pt: '3px',
              }}
            >
              {formatCivilDate(group.date, locale, DATE_SHORT)}
            </Box>
            <Box
              sx={{ display: 'flex', alignItems: 'baseline', gap: '5px', minWidth: 0, pt: '2px' }}
            >
              <Box
                component="span"
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {nameOf(group)}
              </Box>
              {tag(group)}
            </Box>
            {chips(group, false)}
            <Box
              component="span"
              sx={{
                textAlign: 'end',
                fontSize: 13,
                fontVariantNumeric: 'tabular-nums',
                color: toneOf(group.runningMinor),
                pt: '3px',
              }}
            >
              {money(group.runningMinor)}
            </Box>
            {menuFor(group)}
          </Box>
        ) : (
          <Stack
            key={group.key}
            data-forecast-row=""
            sx={{ gap: 1, p: '14px 16px', borderBottom: `1px solid ${tokens.line}` }}
          >
            <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1.25 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" sx={{ alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                  <Box component="span" sx={{ fontSize: 15, fontWeight: 500 }}>
                    {nameOf(group)}
                  </Box>
                  {tag(group)}
                </Stack>
                <Box sx={{ fontSize: 12, color: tokens.textLabel('surface'), mt: '2px' }}>
                  {formatCivilDate(group.date, locale)}
                </Box>
              </Box>
              {menuFor(group)}
            </Stack>
            {chips(group, true)}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
              <Typography variant="micro" sx={{ color: tokens.textLabel('surface') }}>
                {t('virtualAccount.forecast.running')}
              </Typography>
              <Box
                sx={{
                  fontSize: 14,
                  fontVariantNumeric: 'tabular-nums',
                  color: toneOf(group.runningMinor),
                }}
              >
                {money(group.runningMinor)}
              </Box>
            </Box>
          </Stack>
        ),
      )}
      {groups.length === 0 && (
        <Typography
          data-forecast-empty=""
          sx={{ p: '26px 20px', fontSize: 13, lineHeight: 1.6, maxWidth: 560, color: tokens.text2 }}
        >
          {t('virtualAccount.forecast.empty')}
        </Typography>
      )}
    </Paper>
  );

  // Coming from your accounts (item 7): the bank series' slices, grouped by tmm-api's `sliceKey`.
  const coming = comingFromAccounts(full.register);
  const comingSection =
    coming.length === 0 ? null : (
      <Paper
        component="section"
        aria-labelledby="va-coming-title"
        data-coming-from-accounts=""
        sx={{ p: 0, overflow: 'hidden', mb: 2.5 }}
      >
        <Box sx={{ p: '16px 20px', borderBottom: `1px solid ${tokens.line}` }}>
          <Typography id="va-coming-title" variant="h3" component="h2">
            {t('virtualAccount.coming.title')}
          </Typography>
        </Box>
        {coming.map((slice) => {
          const shown = slice.dates.slice(0, COMING_DATES);
          const more = slice.dates.length - shown.length;
          return (
            <Stack
              key={slice.key}
              direction={{ xs: 'column', md: 'row' }}
              data-coming-slice=""
              sx={{
                gap: { xs: 0.5, md: 2 },
                alignItems: { md: 'baseline' },
                p: wide ? '10px 20px' : '12px 16px',
                borderBottom: `1px solid ${tokens.line}`,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box component="span" sx={{ fontSize: 13, fontWeight: 500 }}>
                  {slice.name ?? '—'}
                </Box>
                {slice.accountName !== null && (
                  <Box component="span" sx={{ fontSize: 12, color: tokens.text2 }}>
                    {' '}
                    {t('virtualAccount.coming.from', { account: slice.accountName })}
                  </Box>
                )}
                <Box sx={{ fontSize: 12, color: tokens.text2, fontVariantNumeric: 'tabular-nums' }}>
                  {shown.map((date) => formatCivilDate(date, locale, DATE_SHORT)).join(' · ')}
                  {more > 0 && ` · ${t('virtualAccount.coming.more', { count: more })}`}
                </Box>
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                  color: registerAmountColor(tokens, slice.amountMinor ?? 0),
                  whiteSpace: 'nowrap',
                }}
              >
                {slice.amountMinor === null ? (
                  '—'
                ) : (
                  <MoneyParts minor={slice.amountMinor} currency={currency} signed />
                )}
              </Box>
              {owner && slice.seriesId !== null && (
                <Button
                  size="small"
                  onClick={() => {
                    if (slice.seriesId !== null) void openAccount(slice.seriesId);
                  }}
                  aria-label={t('virtualAccount.coming.openNamed', { name: slice.name ?? '' })}
                  sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                >
                  {t('virtualAccount.coming.open')}
                </Button>
              )}
            </Stack>
          );
        })}
      </Paper>
    );

  return (
    <>
      <ForecastCard
        label={t('virtualAccount.forecast.region', { name: virtualAccount.name })}
        fromToday={fromToday}
        from={full.from}
        to={full.to}
        chart={{
          series: chart,
          events: full.events,
          register: full.register,
          currency,
          minimum: null,
          onDrill: setDrill,
        }}
        kpis={kpis}
        note={t('virtualAccount.forecast.note', { name: virtualAccount.name })}
        filter={filter}
        list={
          <>
            {comingSection}
            {expected.duplicates}
            {expected.prompt}
            {list}
          </>
        }
      />
      {host.node}
      {expected.node}
      {event !== null && (
        <PlannedEventDialog
          action={event.action}
          name={nameOf(event.group)}
          seriesId={event.seriesId}
          originalOn={event.group.originalOn ?? event.group.date}
          onClose={() => setEvent(null)}
          onEdit={(choice) =>
            editScope(event.seriesId, event.group.originalOn ?? event.group.date, choice)
          }
          onDeleted={async () => {
            setEvent(null);
            onNotify(t('plannedEvent.deleted'));
            await reload();
          }}
        />
      )}
    </>
  );
}
